/**
 * Stable API error codes and the canonical error envelope.
 *
 * A code's meaning never changes — clients branch on it. `message` is human-readable and localized;
 * `code` is never localized.
 *
 * See docs/engineering/07-api-conventions.md
 */

export const ERROR_CODES = [
  // authorization and visibility
  'authorization_denied',
  'resource_not_found',

  // concurrency
  'revision_conflict',

  // validation
  'validation_failed',
  'option_not_offered',
  'liturgy_not_ready_for_finalization',
  'planning_item_required',
  'invalid_state_transition',

  // identity and capabilities
  'session_expired',
  'mfa_required',
  'mfa_rejected',
  'capability_expired',
  'capability_revoked',
  'capability_invalid',

  // files
  'attachment_pending_scan',
  'attachment_unavailable',
  'scanner_unavailable',

  // exports
  'export_failed',

  // transport
  'malformed_request',
  'rate_limited',
  'internal_error',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * The HTTP status each code is served with. Kept here so the backend and any client agree, and so
 * the mapping is reviewable in one place.
 *
 * Note `resource_not_found` is deliberately used both for genuine absence and to conceal a private
 * resource from a requester who may not see it. A 403 would confirm existence.
 */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  authorization_denied: 403,
  resource_not_found: 404,

  revision_conflict: 409,

  validation_failed: 422,
  option_not_offered: 422,
  liturgy_not_ready_for_finalization: 422,
  planning_item_required: 422,
  invalid_state_transition: 409,

  session_expired: 401,
  mfa_required: 401,
  mfa_rejected: 401,
  capability_expired: 401,
  capability_revoked: 401,
  capability_invalid: 401,

  attachment_pending_scan: 409,
  attachment_unavailable: 409,
  scanner_unavailable: 503,

  export_failed: 500,

  malformed_request: 400,
  rate_limited: 429,
  internal_error: 500,
};

/** Codes for which retrying the same request unchanged may succeed. */
export const RETRYABLE_ERROR_CODES = [
  'scanner_unavailable',
  'attachment_pending_scan',
  'rate_limited',
  'internal_error',
] as const satisfies readonly ErrorCode[];

export function isRetryable(code: ErrorCode): boolean {
  return (RETRYABLE_ERROR_CODES as readonly ErrorCode[]).includes(code);
}

/**
 * The one error shape the API ever returns.
 */
export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    /** Localized, human-readable. Never parsed by clients. */
    message: string;
    /** Request ID. Surfaced to users as a Reference ID so support can find the server-side event. */
    correlation_id: string;
    /** Field-level detail for validation failures. */
    fields?: Record<string, string>;
    /** Additional machine-readable context. Never contains credentials or private content. */
    details?: Record<string, unknown>;
    retryable: boolean;
  };
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const err = (value as { error: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['message'] === 'string' &&
    typeof candidate['correlation_id'] === 'string'
  );
}
